import { BaseNode, type NodeConfig } from 'mflows';
import { createFunctionContext } from '@/store/dependency.store';
import { isEmpty as isEmptyToolkit, cloneDeep as cloneDeepToolkit, filter as filterToolkit, find as findToolkit } from 'es-toolkit/compat';

// IfElse 节点
interface IfElseConfig extends NodeConfig {
  [key: string]: any;
}
interface IfElseData {
  timestamp: number;
  type: 'if_else';
  branch: 'then' | 'else';
  conditionValue: any;
  payload?: any;
}
export class IfElseNode extends BaseNode<IfElseConfig, any> {
  static NodeType = '条件分支';
  private conditionValue: any;
  private payload: any;
  private lastBranch?: 'then' | 'else';

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', dataType: 'flow', allowTypes: ['flow'], name: '执行' },
      { id: 'condition', dataType: 'any', allowTypes: ['any'], name: '条件' },
      { id: 'input', dataType: 'any', allowTypes: ['any'], name: '输入' }
    ]);
    this.setOutputPorts([
      { id: 'output', dataType: 'any', name: '输出' },
      { id: 'then', dataType: 'flow', name: '是' },
      { id: 'else', dataType: 'flow', name: '否' }
    ]);
  }

  protected receiveData(pointId: string, data: unknown): void {
    if (pointId === 'condition') {
      this.conditionValue = data;
    } else if (pointId === 'input') {
      this.payload = data;
    } else if (pointId === 'execute') {
      const truthy = !!this.conditionValue;
      this.lastBranch = truthy ? 'then' : 'else';
      // 将实际载荷作为事件值传递至下游
      this.setDataChange(this.payload as any);
    }
  }

  protected checkConfig(): boolean {
    return true;
  }

  protected async processConfigUpdate(_: Partial<IfElseConfig>): Promise<any> {
    this.lastBranch = undefined;
    return undefined as any;
  }

  // 根据内部状态选择输出端口
  protected getOutputPortActions(_: string | undefined): { dataOnly?: string[]; triggerFlow?: string[] } {
    const dataOnly = ['output'];
    const triggerFlow = this.lastBranch ? [this.lastBranch] : [];
    return { dataOnly, triggerFlow };
  }
}

// Switch 节点
interface SwitchCase { id: string; value: any; name?: string }
interface SwitchConfig extends NodeConfig {
  cases?: SwitchCase[];
  [key: string]: any;
}
interface SwitchData {
  timestamp: number;
  type: 'switch';
  selectedCaseId?: string;
  key: any;
  payload?: any;
}
export class SwitchNode extends BaseNode<SwitchConfig, any> {
  static NodeType = '多路分支';

  private keyValue: any;
  private payload: any;
  private lastSelectedPort?: string;

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', dataType: 'flow', allowTypes: ['flow'], name: '执行' },
      { id: 'key', dataType: 'any', allowTypes: ['any'], name: '条件' },
      { id: 'input', dataType: 'any', allowTypes: ['any'], name: '输入' }
    ]);
    this.rebuildCaseOutputs();
  }

  protected receiveData(pointId: string, data: unknown): void {
    if (pointId === 'key') {
      this.keyValue = data;
    } else if (pointId === 'input') {
      this.payload = data;
    } else if (pointId === 'execute') {
      const { portId } = this.findMatchingCasePort(this.keyValue);
      this.lastSelectedPort = portId;
      this.setDataChange(this.payload as any);
    }
  }

  protected checkConfig(): boolean {
    return true;
  }

  protected async processConfigUpdate(changes: Partial<SwitchConfig>): Promise<any> {
    if (changes.cases !== undefined) {
      this.settings.cases = changes.cases;
      this.rebuildCaseOutputs();
    }
    this.lastSelectedPort = undefined;
    return undefined as any;
  }

  private rebuildCaseOutputs(): void {
    const cases = this.settings?.cases ?? [];
    const fixed = [
      { id: 'output', dataType: 'any', name: '输出' },
      { id: 'default', dataType: 'flow', name: '默认' }
    ];
    const casePorts = cases.map((c: SwitchCase, idx: number) => ({
      id: `case_${c.id || idx}`,
      dataType: 'flow',
      name: c.name || `分支${idx + 1}`
    }));
    this.setOutputPorts([...fixed, ...casePorts]);
  }

  private findMatchingCasePort(key: any): { portId: string; index: number } {
    const cases: SwitchCase[] = this.settings?.cases ?? [];
    const idx = cases.findIndex(c => c.value === key);
    if (idx >= 0) {
      const c = cases[idx];
      const portId = `case_${c.id ?? idx}`;
      return { portId, index: idx };
    }
    return { portId: 'default', index: -1 };
  }

  protected getOutputPortActions(_: string | undefined): { dataOnly?: string[]; triggerFlow?: string[] } {
    const dataOnly = ['output'];
    const triggerFlow = this.lastSelectedPort ? [this.lastSelectedPort] : ['default'];
    return { dataOnly, triggerFlow };
  }
}

// ForEach 节点
interface ForEachConfig extends NodeConfig { [k: string]: any }
interface ForEachItemData {
  timestamp: number;
  type: 'foreach_item';
  item: any;
  index: number;
  total: number;
}
interface ForEachDoneData {
  timestamp: number;
  type: 'foreach_done';
  total: number;
}

type ForEachData = ForEachItemData | ForEachDoneData;

export class ForEachNode extends BaseNode<ForEachConfig, any> {
  static NodeType = '遍历';

  private listData: any;
  private mode?: 'item' | 'done';

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', dataType: 'flow', allowTypes: ['flow'], name: '执行' },
      { id: 'list', dataType: 'any', allowTypes: ['any'], name: '列表' }
    ]);
    this.setOutputPorts([
      { id: 'element', dataType: 'any', name: '元素' },
      { id: 'item', dataType: 'flow', name: '逐项' },
      { id: 'done', dataType: 'flow', name: '完成' }
    ]);
  }

  protected receiveData(pointId: string, data: unknown): void {
    if (pointId === 'list') {
      this.listData = data;
    } else if (pointId === 'execute') {
      const arr = Array.isArray(this.listData) ? (this.listData as any[]) : [];
      const total = arr.length;
      for (let i = 0; i < total; i++) {
        this.mode = 'item';
        this.setDataChange(arr[i] as any);
      }
      this.mode = 'done';
      this.setDataChange(undefined as any);
    }
  }

  protected checkConfig(): boolean {
    return true;
  }

  protected async processConfigUpdate(_: Partial<ForEachConfig>): Promise<any> {
    this.mode = undefined;
    return undefined as any;
  }

  protected getOutputPortActions(_: string | undefined): { dataOnly?: string[]; triggerFlow?: string[] } {
    if (this.mode === 'item') {
      return { dataOnly: ['element'], triggerFlow: ['item'] };
    }
    if (this.mode === 'done') {
      return { dataOnly: [], triggerFlow: ['done'] };
    }
    return { dataOnly: [], triggerFlow: [] };
  }
}

// 自定义函数节点配置类型
interface CustomFunctionConfig extends NodeConfig {
  code?: string;
  [key: string]: any;
}

// 调试节点配置类型  
interface DebugNodeConfig extends NodeConfig {
  message?: string;
  [key: string]: any;
}

// 自定义函数节点数据类型
interface CustomFunctionData {
  timestamp: number;
  type: 'custom_function';
  result?: any;
}

// 调试节点数据类型
interface DebugNodeData {
  timestamp: number;
  type: 'debug';
  value?: any;
}

// 自定义函数节点类
export class CustomFunction extends BaseNode<CustomFunctionConfig, CustomFunctionData> {
  static NodeType = '自定义方法';

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', dataType: 'flow', allowTypes: ['flow'], name: '执行' },
      { id: 'input', dataType: 'any', allowTypes: ['any'], name: '输入' }
    ]);
    this.setOutputPorts([
      { id: 'output', dataType: 'any', name: '输出' },
      { id: 'next', dataType: 'flow', name: '下一步' }
    ]);
  }

  protected receiveData(pointId: string, data: unknown): void {
    const inputPoint = this.findInputPort(pointId);
    
    if (pointId === 'input') {
      // 数据输入端口：保存数据但不执行
      this.updateSettings({ inputData: data });
    } else if (pointId === 'execute' && inputPoint?.dataType === 'flow') {
      // 流控端口：触发执行
      this.processFunction(this.settings.inputData);
    }
  }

  protected checkConfig(): boolean {
    return typeof this.settings.code === 'string' && this.settings.code.length > 0;
  }

  protected async processConfigUpdate(changes: Partial<CustomFunctionConfig>): Promise<CustomFunctionData> {
    if (changes.code !== undefined) {
      this.settings.code = changes.code;
    }
    
    return {
      timestamp: Date.now(),
      type: 'custom_function',
      result: null
    };
  }

  private async processFunction(inputData: unknown): Promise<void> {
    try {
      // 执行自定义函数代码
      const context = createFunctionContext();
      // 兼容历史脚本：同时提供 input / payload / value 三个别名，均指向输入数据
      const func = new Function(
        'input',
        'payload',
        'value',
        'context',
        `with(context){ ${this.settings.code || 'return input;'} }`
      );
      const result = func(inputData, inputData, inputData, context);
      
      const data: CustomFunctionData = {
        timestamp: Date.now(),
        type: 'custom_function',
        result: result
      };
      
      this.setDataChange(data);
    } catch (error) {
      console.error('自定义函数执行错误:', error);
    }
  }
}

// 调试节点类
export class DebugNode extends BaseNode<DebugNodeConfig, DebugNodeData> {
  static NodeType = '调试节点';

  protected async initialize(): Promise<void> {
    // 输出型节点不需要执行流输入/输出，仅保留数据输入
    this.setInputPorts([
      { id: 'input', dataType: 'any', allowTypes: ['any'], name: '输入' }
    ]);
    this.setOutputPorts([]);
   }

  protected receiveData(pointId: string, data: unknown): void {
    void pointId; void data;
    if (pointId === 'input') {
      this.debugOutput(data);
    }
  }

  protected checkConfig(): boolean {
    return true; // 调试节点总是有效的
  }

  protected async processConfigUpdate(changes: Partial<DebugNodeConfig>): Promise<DebugNodeData> {
    if (changes.message !== undefined) {
      this.settings.message = changes.message;
    }
    
    return {
      timestamp: Date.now(),
      type: 'debug',
      value: null
    };
  }

  private debugOutput(inputData: unknown): void {
    const data: DebugNodeData = {
      timestamp: Date.now(),
      type: 'debug',
      value: inputData
    };
    this.setDataChange(data);
  }
}

// ================= 数据处理（基于 es-toolkit）节点 =================

// 1) 是否为空
interface IsEmptyConfig extends NodeConfig { }
interface IsEmptyData { timestamp: number; type: 'is_empty'; result: boolean; input?: any }
export class IsEmptyNode extends BaseNode<IsEmptyConfig, IsEmptyData> {
  static NodeType = '是否为空';

  private inputData: any;

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', dataType: 'flow', allowTypes: ['flow'], name: '执行' },
      { id: 'input', dataType: 'any', allowTypes: ['any'], name: '输入' }
    ]);
    this.setOutputPorts([
      { id: 'result', dataType: 'boolean', name: '结果' },
      { id: 'next', dataType: 'flow', name: '下一步' }
    ]);
  }

  protected receiveData(pointId: string, data: unknown): void {
    if (pointId === 'input') {
      this.inputData = data;
    } else if (pointId === 'execute') {
      const res = isEmptyToolkit(this.inputData);
      this.setDataChange({ timestamp: Date.now(), type: 'is_empty', result: res, input: this.inputData });
    }
  }

  protected checkConfig(): boolean { return true }
  protected async processConfigUpdate(_: Partial<IsEmptyConfig>): Promise<IsEmptyData> {
    return { timestamp: Date.now(), type: 'is_empty', result: false };
  }
}

// 2) 深拷贝
interface CloneDeepConfig extends NodeConfig { }
interface CloneDeepData { timestamp: number; type: 'clone_deep'; value: any }
export class CloneDeepNode extends BaseNode<CloneDeepConfig, CloneDeepData> {
  static NodeType = '深拷贝';

  private inputData: any;

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', dataType: 'flow', allowTypes: ['flow'], name: '执行' },
      { id: 'input', dataType: 'any', allowTypes: ['any'], name: '输入' }
    ]);
    this.setOutputPorts([
      { id: 'output', dataType: 'any', name: '输出' },
      { id: 'next', dataType: 'flow', name: '下一步' }
    ]);
  }

  protected receiveData(pointId: string, data: unknown): void {
    if (pointId === 'input') {
      this.inputData = data;
    } else if (pointId === 'execute') {
      const cloned = cloneDeepToolkit(this.inputData);
      this.setDataChange({ timestamp: Date.now(), type: 'clone_deep', value: cloned });
    }
  }

  protected checkConfig(): boolean { return true }
  protected async processConfigUpdate(_: Partial<CloneDeepConfig>): Promise<CloneDeepData> {
    return { timestamp: Date.now(), type: 'clone_deep', value: undefined } as any;
  }
}

// 3) 过滤
interface FilterConfig extends NodeConfig { }
interface FilterData { timestamp: number; type: 'filter'; list: any[] }
export class FilterNode extends BaseNode<FilterConfig, FilterData> {
  static NodeType = '过滤';

  private listData: any[] = [];
  private key?: string;
  private expect?: any;

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', dataType: 'flow', allowTypes: ['flow'], name: '执行' },
      { id: 'list', dataType: 'any', allowTypes: ['any'], name: '列表' },
      { id: 'key', dataType: 'string', allowTypes: ['string', 'text'], name: '键' },
      { id: 'value', dataType: 'any', allowTypes: ['any'], name: '值' }
    ]);
    this.setOutputPorts([
      { id: 'result', dataType: 'array', name: '结果' },
      { id: 'next', dataType: 'flow', name: '下一步' }
    ]);
  }

  protected receiveData(pointId: string, data: unknown): void {
    if (pointId === 'list') {
      this.listData = Array.isArray(data) ? (data as any[]) : [];
    } else if (pointId === 'key') {
      this.key = String(data ?? '');
    } else if (pointId === 'value') {
      this.expect = data;
    } else if (pointId === 'execute') {
      const k = this.key;
      const v = this.expect;
      const getter = (obj: any, path?: string) => {
        if (!path) return obj;
        return String(path).split('.').reduce((acc, p) => acc?.[p], obj);
      };
      const res = filterToolkit(this.listData, (item: any) => {
        const val = getter(item, k);
        return val === v;
      });
      this.setDataChange({ timestamp: Date.now(), type: 'filter', list: res as any[] });
    }
  }

  protected checkConfig(): boolean { return true }
  protected async processConfigUpdate(_: Partial<FilterConfig>): Promise<FilterData> {
    return { timestamp: Date.now(), type: 'filter', list: [] };
  }
}

// 4) 查找
interface FindConfig extends NodeConfig { }
interface FindData { timestamp: number; type: 'find'; item: any }
export class FindNode extends BaseNode<FindConfig, FindData> {
  static NodeType = '查找';

  private listData: any[] = [];
  private key?: string;
  private expect?: any;

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', dataType: 'flow', allowTypes: ['flow'], name: '执行' },
      { id: 'list', dataType: 'any', allowTypes: ['any'], name: '列表' },
      { id: 'key', dataType: 'string', allowTypes: ['string', 'text'], name: '键' },
      { id: 'value', dataType: 'any', allowTypes: ['any'], name: '值' }
    ]);
    this.setOutputPorts([
      { id: 'result', dataType: 'any', name: '结果' },
      { id: 'next', dataType: 'flow', name: '下一步' }
    ]);
  }

  protected receiveData(pointId: string, data: unknown): void {
    if (pointId === 'list') {
      this.listData = Array.isArray(data) ? (data as any[]) : [];
    } else if (pointId === 'key') {
      this.key = String(data ?? '');
    } else if (pointId === 'value') {
      this.expect = data;
    } else if (pointId === 'execute') {
      const k = this.key;
      const v = this.expect;
      const getter = (obj: any, path?: string) => {
        if (!path) return obj;
        return String(path).split('.').reduce((acc, p) => acc?.[p], obj);
      };
      const item = findToolkit(this.listData, (it: any) => {
        const val = getter(it, k);
        return val === v;
      });
      this.setDataChange({ timestamp: Date.now(), type: 'find', item });
    }
  }

  protected checkConfig(): boolean { return true }
  protected async processConfigUpdate(_: Partial<FindConfig>): Promise<FindData> {
    return { timestamp: Date.now(), type: 'find', item: undefined } as any;
  }
}